import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, FolderOpen, ChevronRight, Image, X, Check, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Image Picker Component
const ImagePicker = ({ selectedImage, onSelect, onClose, token }) => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/upload/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data.images.map(img => img.url));
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      onSelect(response.data.url);
      onClose();
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background w-full max-w-2xl mx-4 max-h-[70vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-medium">Select Category Image</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b">
          <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed cursor-pointer hover:border-primary">
            <Upload className="w-4 h-4" />
            <span className="text-sm">{uploading ? 'Uploading...' : 'Upload new image'}</span>
            <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
          </label>
        </div>
        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-4 gap-2">
          {images.map((img, i) => (
            <div
              key={i}
              onClick={() => { onSelect(img); onClose(); }}
              className={`aspect-square cursor-pointer border-2 ${selectedImage === img ? 'border-primary' : 'border-transparent hover:border-gray-300'}`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [productCounts, setProductCounts] = useState({});
  const token = localStorage.getItem('chaleur_admin_token');

  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    image: '',
    order: 0
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data || []);
      
      // Get product counts per category
      const counts = {};
      for (const cat of response.data || []) {
        try {
          const prodRes = await axios.get(`${API}/products`, { params: { category: cat.slug, limit: 1 } });
          counts[cat.id] = prodRes.data.total || 0;
        } catch (e) {
          counts[cat.id] = 0;
        }
      }
      setProductCounts(counts);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/categories`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Category created');
      }
      setShowForm(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setForm({
      name: category.name || '',
      slug: category.slug || '',
      description: category.description || '',
      image: category.image || '',
      order: category.order || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Category deleted');
      setDeleteConfirm(null);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const resetForm = () => {
    setForm({ name: '', slug: '', description: '', image: '', order: 0 });
    setEditingCategory(null);
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  return (
    <div className="space-y-6" data-testid="admin-categories">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-light mb-2">Categories</h1>
          <p className="text-muted-foreground">Manage product categories ({categories.length})</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowForm(true); }}
          className="rounded-none bg-foreground text-primary-foreground"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Category
        </Button>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <Card key={i} className="rounded-none animate-pulse">
              <div className="h-40 bg-gray-200" />
              <CardContent className="p-4">
                <div className="h-4 bg-gray-200 w-2/3 mb-2" />
                <div className="h-3 bg-gray-200 w-1/2" />
              </CardContent>
            </Card>
          ))
        ) : categories.length > 0 ? (
          categories.map(category => (
            <Card key={category.id} className="rounded-none overflow-hidden group">
              <div className="h-40 bg-gray-100 relative">
                {category.image ? (
                  <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FolderOpen className="w-12 h-12 text-gray-300" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button size="sm" variant="secondary" onClick={() => handleEdit(category)} className="rounded-none">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => setDeleteConfirm(category)} className="rounded-none">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="font-medium mb-1">{category.name}</h3>
                <p className="text-sm text-muted-foreground mb-2">/{category.slug}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {productCounts[category.id] || 0} products
                  </span>
                  <span className="text-xs bg-gray-100 px-2 py-1">Order: {category.order || 0}</span>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="col-span-full p-8 text-center text-muted-foreground">
            No categories yet. Create your first category.
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <Dialog open={true} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Category</DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Are you sure you want to delete "{deleteConfirm.name}"? Products in this category won't be deleted.
            </p>
            <div className="flex gap-4 justify-end mt-4">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="rounded-none">Cancel</Button>
              <Button onClick={() => handleDelete(deleteConfirm.id)} className="rounded-none bg-red-600 hover:bg-red-700 text-white">
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Category Form */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'New Category'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="mb-2 block">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ 
                  ...form, 
                  name: e.target.value,
                  slug: editingCategory ? form.slug : generateSlug(e.target.value)
                })}
                className="rounded-none"
                required
              />
            </div>
            <div>
              <Label className="mb-2 block">Slug</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="rounded-none"
                required
              />
            </div>
            <div>
              <Label className="mb-2 block">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="rounded-none min-h-[80px]"
              />
            </div>
            <div>
              <Label className="mb-2 block">Category Image</Label>
              <div className="flex gap-3 items-center">
                <Button type="button" variant="outline" onClick={() => setShowImagePicker(true)} className="rounded-none">
                  <FolderOpen className="w-4 h-4 mr-2" /> Select Image
                </Button>
                {form.image && (
                  <div className="relative w-16 h-16 group">
                    <img src={form.image} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, image: '' })}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Display Order</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                className="rounded-none w-24"
              />
              <p className="text-xs text-muted-foreground mt-1">Lower numbers appear first</p>
            </div>
            <div className="flex gap-4 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-none">
                Cancel
              </Button>
              <Button type="submit" className="rounded-none bg-foreground text-primary-foreground">
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Image Picker */}
      {showImagePicker && (
        <ImagePicker
          selectedImage={form.image}
          onSelect={(url) => setForm({ ...form, image: url })}
          onClose={() => setShowImagePicker(false)}
          token={token}
        />
      )}
    </div>
  );
};

export default AdminCategories;
